import { useCallback, useState } from "react";
import { calendarService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  ICalendarConfig,
  ICalendarSet,
  ITrayConfig,
  WorkTemplateBase,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getCalendarSetUUID = createUUID();

export const useCalendarSet = () => {
  const [calendarSet, setCalendarSet] = useState<ICalendarSet | null>(null);
  const [activeCalendar, setActiveCalendar] = useState<ICalendarConfig | null>(
    null
  );
  const stateManager = useComponentState();

  const getCalendarSet = useCallback(
    (id?: string) => {
      stateManager.abort(getCalendarSetUUID);
      if (!id) {
        setCalendarSet(null);
        setActiveCalendar(null);
        return;
      }

      stateManager.run(async () => {
        const response = await calendarService.calendarSets.getById({
          id,
        });

        if (response.status === HTTP_STATUSES.OK) {
          setCalendarSet(response.data);
          const calendarSet = response.data;
          const calendarConfigs = (calendarSet.calendarConfigs || []).map(
            (calendar: ICalendarConfig) => {
              calendar.trays = (calendarSet.trays || []).filter(
                (tray: ITrayConfig) => tray.isActive
              );
              calendar.orderTemplates = calendarSet.workTemplateBases || [];
              calendar.orderTemplateIds = (
                calendarSet.workTemplateBases || []
              ).map((template: WorkTemplateBase) => template.id);
              calendar.assignableUserIds = (calendar.assignableUsers || []).map(
                (user) => user.id
              );
              calendar.serviceAreas = calendarSet.serviceAreas || [];
              return calendar;
            }
          );
          setActiveCalendar(
            calendarConfigs.length > 0 ? calendarConfigs[0] : null
          );
        }
      }, getCalendarSetUUID);
    },
    [stateManager, setCalendarSet, setActiveCalendar]
  );

  const changeActiveCalendar = useCallback(
    (calendarId: string) => {
      if (!calendarSet?.calendarConfigs) {
        setActiveCalendar(null);
        return;
      }
      const newActiveCalendar = calendarSet.calendarConfigs.find(
        (calendar) => calendar.id === calendarId
      );
      if (newActiveCalendar) {
        setActiveCalendar(newActiveCalendar);
      }
    },
    [calendarSet, setActiveCalendar]
  );

  return {
    loading: stateManager.isBusy(),
    calendarSet,
    getCalendarSet,
    activeCalendar,
    changeActiveCalendar,
  };
};
